import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  IconButton,
  Typography,
  MenuItem,
} from "@mui/material";

import { AddIcon, DeleteIcon } from "../../../../2_ksbHub/icons/icons";

import { useEffect, useState } from "react";

import { useBszFinderContext } from "../../context/bszContext";
import KsbDialogActions from "../../../../2_ksbHub/components/KsbDialogActions";

const GRADES = [1, 2, 3, 4, 5, 6];

export default function BszClassesAdminDialog({ open, onClose }) {
  const { classes, saveClasses } = useBszFinderContext();

  const [localClasses, setLocalClasses] = useState([]);
  const [originalClasses, setOriginalClasses] = useState([]);
  const [newClass, setNewClass] = useState("");
  const [newGradeId, setNewGradeId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLocalClasses(classes || []);
      setOriginalClasses(classes || []);
      setNewClass("");
      setNewGradeId("");
    }
  }, [open, classes]);

  const isNew = (c) => {
    return !originalClasses.find((o) => o.id === c.id);
  };

  const isChanged = (c) => {
    const original = originalClasses.find((o) => o.id === c.id);

    if (!original) return false;

    return original.name !== c.name || original.gradeId !== c.gradeId;
  };

  const handleAdd = () => {
    const trimmed = newClass.trim();
    const gradeId = Number(newGradeId);

    if (!trimmed) return;
    if (gradeId < 1 || gradeId > 6) return;

    setLocalClasses((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: trimmed,
        gradeId,
        grade: gradeId,
      },
    ]);

    setNewClass("");
    setNewGradeId("");
  };

  const handleDelete = (id) => {
    setLocalClasses((prev) => prev.filter((c) => c.id !== id));
  };

  const handleChange = (id, value) => {
    setLocalClasses((prev) =>
      prev.map((c) => (c.id === id ? { ...c, name: value } : c)),
    );
  };

  const handleGradeChange = (id, value) => {
    const gradeId = Number(value);

    setLocalClasses((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              gradeId,
              grade: gradeId,
            }
          : c,
      ),
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const cleaned = localClasses
        .map((c) => ({
          ...c,
          name: c.name.trim(),
          gradeId: Number(c.gradeId),
        }))
        .filter((c) => c.name && c.gradeId >= 1 && c.gradeId <= 6);

      await saveClasses(cleaned);

      onClose();
    } catch (err) {
      console.error("saving classes failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Klassenverwaltung Finder</DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {localClasses.map((c) => {
            const newItem = isNew(c);
            const changedItem = isChanged(c);

            return (
              <Box
                key={c.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 0.5,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: newItem
                    ? "rgba(76,175,80,0.12)"
                    : changedItem
                      ? "rgba(255,193,7,0.15)"
                      : "transparent",
                }}
              >
                <TextField
                  value={c.name}
                  onChange={(e) => handleChange(c.id, e.target.value)}
                  size="small"
                  fullWidth
                />

                <TextField
                  select
                  value={c.gradeId ?? ""}
                  onChange={(e) => handleGradeChange(c.id, e.target.value)}
                  size="small"
                  label="Stufe"
                  sx={{ width: 90, flexShrink: 0 }}
                >
                  {GRADES.map((grade) => (
                    <MenuItem key={grade} value={grade}>
                      {grade}
                    </MenuItem>
                  ))}
                </TextField>

                {newItem && (
                  <Typography variant="caption" color="success.main">
                    neu
                  </Typography>
                )}

                {!newItem && changedItem && (
                  <Typography variant="caption" color="warning.main">
                    geändert
                  </Typography>
                )}

                <IconButton onClick={() => handleDelete(c.id)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            );
          })}

          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <TextField
              value={newClass}
              onChange={(e) => setNewClass(e.target.value)}
              placeholder="Neue Klasse..."
              size="small"
              fullWidth
            />

            <TextField
              select
              value={newGradeId}
              onChange={(e) => setNewGradeId(e.target.value)}
              size="small"
              label="Stufe"
              sx={{ width: 90 }}
            >
              {GRADES.map((grade) => (
                <MenuItem key={grade} value={grade}>
                  {grade}
                </MenuItem>
              ))}
            </TextField>

            <IconButton onClick={handleAdd}>
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <KsbDialogActions
        onCancel={onClose}
        onConfirm={handleSave}
        confirmLabel="Speichern"
        confirmDisabled={saving}
        confirmLoading={saving}
      />
    </Dialog>
  );
}
